// client/src/pages/MinistryListPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/api";
import { toast } from "react-hot-toast";
import "./MinistryListPage.css";

/* ---------- Add Ministry Modal ---------- */
function AddMinistryModal({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setActive(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSave = async () => {
    const payload = { name: name.trim(), active: !!active };
    if (!payload.name) {
      toast.error("Please enter a ministry name.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ministries", payload);
      toast.success("Ministry created.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Create failed.");
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Escape") onClose?.();
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleSave();
  };

  if (!open) return null;
  return (
    <div className="modal-overlay" onKeyDown={onKey}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-ministry-title"
      >
        <div className="modal-header">
          <div id="add-ministry-title" className="modal-title">
            + Add Ministry
          </div>
          <button className="modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <label>
            Name
            <input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g., Children's Choir`}
            />
          </label>

          <label>
            Status
            <select
              value={active ? "true" : "false"}
              onChange={(e) => setActive(e.target.value === "true")}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </label>
        </div>

        <div className="modal-footer">
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            title="Ctrl/Cmd + Enter to save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Edit (inline) Modal ---------- */
function EditMinistryModal({ row, onClose, onSaved }) {
  const [name, setName] = useState(row?.name || "");
  const [active, setActive] = useState(!!row?.active);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setName(row?.name || "");
    setActive(!!row?.active);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [row]);

  const handleSave = async () => {
    const payload = { name: name.trim(), active: !!active };
    if (!payload.name) {
      toast.error("Please enter a ministry name.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/ministries/${row.id}`, payload);
      toast.success("Ministry updated.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Escape") onClose?.();
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleSave();
  };

  if (!row) return null;
  return (
    <div className="modal-overlay" onKeyDown={onKey}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-ministry-title"
      >
        <div className="modal-header">
          <div id="edit-ministry-title" className="modal-title">
            Edit Ministry
          </div>
          <button className="modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <label>
            Name
            <input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label>
            Status
            <select
              value={active ? "true" : "false"}
              onChange={(e) => setActive(e.target.value === "true")}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </label>
        </div>

        <div className="modal-footer">
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            title="Ctrl/Cmd + Enter to save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function MinistryListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ministries");
      const rows = (res.data || []).map((r) => ({
        ...r,
        active: r.active === true || r.active === "true" || r.active === 1,
      }));
      setItems(rows);
    } catch {
      toast.error("Failed to load ministries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((m) => (m.name || "").toLowerCase().includes(needle));
  }, [items, q]);

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/ministries/${row.id}`);
      toast.success("Ministry deleted.");
      fetchAll();
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          "Delete failed. Remove relations first if your DB blocks it."
      );
    }
  };

  return (
    <div className="ministries-page">
      <h2>🏷️ Ministries</h2>

      <div className="ministry-form">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search ministries..."
        />
        <button type="button" onClick={() => setShowAdd(true)}>
          + Add Ministry
        </button>
      </div>

      <div className="table-wrapper">
        <table className="ministries-table">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ width: 140 }}>Status</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: 16 }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: 16 }}>
                  No ministries
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>
                    <span className={`pill ${m.active ? "on" : "off"}`}>
                      {m.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => setEditingRow(m)}
                    >
                      Edit
                    </button>
                    <button className="danger" onClick={() => remove(m)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Add ministry modal */}
      <AddMinistryModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={fetchAll}
      />

      {/* Edit ministry modal */}
      <EditMinistryModal
        row={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={fetchAll}
      />
    </div>
  );
}
